import { FirebaseApp } from 'firebase/app';
import { 
  getAnalytics, 
  Analytics, 
  logEvent, 
  setUserProperties, 
  setAnalyticsCollectionEnabled 
} from 'firebase/analytics';
import { 
  FactionType, 
  CampaignType, 
  UnitType, 
  TerrainType,
  BattleStats 
} from '../types';
import app from '../firebase'; // Use existing Firebase app instance

interface GameSessionData {
  sessionId: string;
  startTime: number;
  campaign: CampaignType;
  gameMode: 'PvP' | 'PvE' | 'Online';
  playerFaction: FactionType;
  opponentFaction: FactionType;
  mapSize: { width: number; height: number };
  aiDifficulty?: string;
}

interface BattleEventData {
  attacker_faction: FactionType;
  attacker_unit: UnitType;
  attacker_level: number;
  defender_faction: FactionType;
  defender_unit: UnitType;
  defender_level: number;
  outcome: 'attacker_won' | 'defender_won' | 'draw';
  damage_dealt: number;
  terrain: TerrainType;
  turn: number;
}


class AnalyticsService {
  private static instance: AnalyticsService | null = null;
  private app: FirebaseApp | null = null;
  private analytics: Analytics | null = null;
  private sessionStartTime: number;
  private currentSessionId: string;
  private currentGameSession: GameSessionData | null = null;
  private turnStartTime: number = 0;
  private turnActions = {
    unitsMoved: 0,
    battlesFought: 0,
    unitsProduced: 0,
    resourcesSpent: 0
  };
  private isEnabled: boolean = false;
  private totalGamesPlayed: number = 0;
  private totalPlaytime: number = 0;
  private favoriteFactionsMap: Map<FactionType, number> = new Map();

  private constructor() {
    this.sessionStartTime = Date.now();
    this.currentSessionId = this.generateSessionId();
    this.loadUserStats();
    this.checkConsent();
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  private checkConsent(): void {
    const consent = localStorage.getItem('analytics_consent');
    if (consent === 'granted') {
      this.initialize();
    }
  }

  public async requestConsent(): Promise<boolean> {
    // This will be called from the UI
    return new Promise((resolve) => {
      // The UI will handle showing the consent dialog
      // For now, we'll just check if consent was previously given
      const consent = localStorage.getItem('analytics_consent');
      resolve(consent === 'granted');
    });
  }

  public grantConsent(): void {
    localStorage.setItem('analytics_consent', 'granted');
    this.initialize();
  }

  public revokeConsent(): void {
    localStorage.setItem('analytics_consent', 'denied');
    this.disable();
  }

  private initialize(): void {
    if (!this.app) {
      try {
        this.app = app; // Use the existing Firebase app instance
        this.analytics = getAnalytics(this.app);
        this.isEnabled = true;
        setAnalyticsCollectionEnabled(this.analytics, true);
        this.trackSessionStart();
        this.updateUserProperties();
      } catch (error) {
        console.warn('Firebase Analytics initialization failed:', error);
        this.isEnabled = false;
      }
    }
  }

  private disable(): void {
    if (this.analytics) {
      setAnalyticsCollectionEnabled(this.analytics, false);
    }
    this.isEnabled = false;
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private loadUserStats(): void {
    const stats = localStorage.getItem('user_game_stats');
    if (stats) {
      const parsed = JSON.parse(stats);
      this.totalGamesPlayed = parsed.totalGamesPlayed || 0;
      this.totalPlaytime = parsed.totalPlaytime || 0;
      this.favoriteFactionsMap = new Map(parsed.favoriteFactions || []);
    }
  }

  private saveUserStats(): void {
    const stats = {
      totalGamesPlayed: this.totalGamesPlayed,
      totalPlaytime: this.totalPlaytime,
      favoriteFactions: Array.from(this.favoriteFactionsMap.entries())
    };
    localStorage.setItem('user_game_stats', JSON.stringify(stats));
  }

  private updateUserProperties(): void {
    if (!this.isEnabled || !this.analytics) return;

    const favoriteFactionsArray = Array.from(this.favoriteFactionsMap.entries());
    const favoriteFaction = favoriteFactionsArray.length > 0
      ? favoriteFactionsArray.reduce((a, b) => a[1] > b[1] ? a : b)[0]
      : 'none';

    const playerLevel = this.calculatePlayerLevel();

    setUserProperties(this.analytics, {
      total_games_played: this.totalGamesPlayed,
      player_level: playerLevel,
      favorite_faction: favoriteFaction,
      total_playtime_hours: Math.floor(this.totalPlaytime / 3600),
      device_type: this.getDeviceType()
    });
  }

  private calculatePlayerLevel(): string {
    if (this.totalGamesPlayed < 5) return 'beginner';
    if (this.totalGamesPlayed < 20) return 'intermediate';
    if (this.totalGamesPlayed < 50) return 'experienced';
    return 'veteran';
  }

  private getDeviceType(): string {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  // Session tracking
  public trackSessionStart(): void {
    if (!this.isEnabled || !this.analytics) return;

    logEvent(this.analytics, 'session_start', {
      session_id: this.currentSessionId,
      timestamp: this.sessionStartTime
    });
  }

  public trackSessionEnd(): void {
    if (!this.isEnabled || !this.analytics) return;

    const duration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
    this.totalPlaytime += duration;
    this.saveUserStats();

    logEvent(this.analytics, 'session_end', {
      session_id: this.currentSessionId,
      duration_seconds: duration
    });
  }

  // Game flow tracking
  public trackGameStart(params: {
    campaign: CampaignType;
    gameMode: 'PvP' | 'PvE' | 'Online';
    playerFaction: FactionType;
    opponentFaction: FactionType;
    mapWidth: number;
    mapHeight: number;
    aiDifficulty?: string;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    this.currentGameSession = {
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      campaign: params.campaign,
      gameMode: params.gameMode,
      playerFaction: params.playerFaction,
      opponentFaction: params.opponentFaction,
      mapSize: { width: params.mapWidth, height: params.mapHeight },
      aiDifficulty: params.aiDifficulty
    };

    // Update faction preference
    const count = this.favoriteFactionsMap.get(params.playerFaction) || 0;
    this.favoriteFactionsMap.set(params.playerFaction, count + 1);

    this.totalGamesPlayed++;
    this.saveUserStats();
    this.updateUserProperties();

    logEvent(this.analytics, 'game_start', {
      campaign: params.campaign,
      game_mode: params.gameMode,
      player_faction: params.playerFaction,
      opponent_faction: params.opponentFaction,
      map_width: params.mapWidth,
      map_height: params.mapHeight,
      ai_difficulty: params.aiDifficulty || 'none'
    });
  }

  public trackGameEnd(params: {
    winner: FactionType;
    endReason: 'victory' | 'defeat' | 'resign' | 'disconnect';
    turnsPlayed: number;
    battleStats: Map<FactionType, BattleStats>;
  }): void {
    if (!this.isEnabled || !this.analytics || !this.currentGameSession) return;

    const duration = Math.floor((Date.now() - this.currentGameSession.startTime) / 1000);
    const playerStats = params.battleStats.get(this.currentGameSession.playerFaction);
    const opponentStats = params.battleStats.get(this.currentGameSession.opponentFaction);

    logEvent(this.analytics, 'game_end', {
      campaign: this.currentGameSession.campaign,
      game_mode: this.currentGameSession.gameMode,
      winner: params.winner,
      end_reason: params.endReason,
      duration_seconds: duration,
      turns_played: params.turnsPlayed,
      player_units_killed: playerStats?.enemiesKilled || 0,
      player_units_lost: playerStats?.unitsLost || 0,
      player_cities_captured: playerStats?.citiesCaptured || 0,
      opponent_units_killed: opponentStats?.enemiesKilled || 0,
      opponent_units_lost: opponentStats?.unitsLost || 0
    });

    this.currentGameSession = null;
  }

  // Turn tracking
  public trackTurnStart(_faction: FactionType): void {
    this.turnStartTime = Date.now();
    this.turnActions = {
      unitsMoved: 0,
      battlesFought: 0,
      unitsProduced: 0,
      resourcesSpent: 0
    };
  }

  public trackTurnEnd(faction: FactionType, turnNumber: number): void {
    if (!this.isEnabled || !this.analytics) return;

    const duration = Math.floor((Date.now() - this.turnStartTime) / 1000);

    logEvent(this.analytics, 'turn_completed', {
      turn_number: turnNumber,
      faction: faction,
      duration_seconds: duration,
      units_moved: this.turnActions.unitsMoved,
      battles_fought: this.turnActions.battlesFought,
      units_produced: this.turnActions.unitsProduced,
      resources_spent: this.turnActions.resourcesSpent
    });
  }

  // Action tracking
  public trackUnitAction(action: {
    unitType: UnitType;
    actionType: 'move' | 'attack' | 'build' | 'heal' | 'repair' | 'board' | 'disembark';
    faction: FactionType;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    if (action.actionType === 'move') {
      this.turnActions.unitsMoved++;
    }

    logEvent(this.analytics, 'unit_action', {
      unit_type: action.unitType,
      action_type: action.actionType,
      faction: action.faction
    });
  }

  public trackBattle(battle: BattleEventData): void {
    if (!this.isEnabled || !this.analytics) return;

    this.turnActions.battlesFought++;

    logEvent(this.analytics, 'battle_occurred', battle);
  }

  public trackCityCapture(params: {
    cityName: string;
    capturingFaction: FactionType;
    previousOwner: FactionType | null;
    turn: number;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    logEvent(this.analytics, 'city_captured', {
      city_name: params.cityName,
      capturing_faction: params.capturingFaction,
      previous_owner: params.previousOwner || 'neutral',
      turn: params.turn
    });
  }

  public trackUnitProduction(params: {
    unitType: UnitType;
    faction: FactionType;
    cityLevel: number;
    cost: number;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    this.turnActions.unitsProduced++;
    this.turnActions.resourcesSpent += params.cost;

    logEvent(this.analytics, 'unit_produced', {
      unit_type: params.unitType,
      faction: params.faction,
      city_level: params.cityLevel,
      cost: params.cost
    });
  }

  public trackCityUpgrade(params: {
    cityName: string;
    fromLevel: number;
    toLevel: number;
    faction: FactionType;
    cost: number;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    this.turnActions.resourcesSpent += params.cost;

    logEvent(this.analytics, 'city_upgraded', {
      city_name: params.cityName,
      from_level: params.fromLevel,
      to_level: params.toLevel,
      faction: params.faction,
      cost: params.cost
    });
  }

  public trackMenuInteraction(params: {
    menuType: 'main' | 'campaign' | 'settings' | 'save' | 'load';
    selection: string;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    logEvent(this.analytics, 'menu_interaction', {
      menu_type: params.menuType,
      selection: params.selection
    });
  }

  public trackMultiplayerEvent(params: {
    eventType: 'match_start' | 'match_end' | 'player_joined' | 'player_left';
    isOnline: boolean;
    playerCount?: number;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    logEvent(this.analytics, 'multiplayer_event', {
      event_type: params.eventType,
      is_online: params.isOnline,
      player_count: params.playerCount || 2
    });
  }

  public trackPerformance(params: {
    fps: number;
    renderTime: number;
    turnProcessingTime?: number;
  }): void {
    if (!this.isEnabled || !this.analytics) return;

    // Only log performance issues
    if (params.fps < 30 || params.renderTime > 100) {
      logEvent(this.analytics, 'performance_issue', {
        fps: Math.round(params.fps),
        render_time_ms: Math.round(params.renderTime),
        turn_processing_ms: params.turnProcessingTime ? Math.round(params.turnProcessingTime) : 0
      });
    }
  }
}

export default AnalyticsService;